import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Button from '../components/UI/Button';
import Card from '../components/UI/Card';

const ReviewsScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedReview, setSelectedReview] = useState(null);

  const rawReviews = t('reviews.testimonials', { returnObjects: true }) || [];

  // Add default avatars and highlights to reviews from translation files
  const defaultAvatars = ['👩🏻‍💼', '👨🏼‍💻', '👩🏽‍🎨', '👩🏻‍🎓', '👨🏻‍🏫', '👩🏽‍💼'];

  const reviews = rawReviews.map((review, index) => ({
    ...review,
    avatar: review.avatar || defaultAvatars[index % defaultAvatars.length],
    text: review.text || review.review || '',
    highlights: review.highlights || []
  }));

  const stats = [
    { label: 'Average Rating', value: t('reviews.averageRating'), icon: '⭐' },
    { label: 'Total Reviews', value: t('reviews.totalReviews'), icon: '💬' },
    { label: 'Repeat Guests', value: t('reviews.repeatGuests'), icon: '🔄' },
    { label: 'Response Rate', value: t('reviews.responseRate'), icon: '⚡' }
  ];

  const StarRating = ({ rating }) => {
    return (
      <div className="flex">
        {[...Array(5)].map((_, i) => (
          <svg
            key={i}
            className={`w-5 h-5 ${i < rating ? 'text-accent' : 'text-gray-300'}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen  ">
      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-black bg-opacity-60"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat mix-blend-overlay"
          style={{
            backgroundImage: `url('/assets/images/villa Baidane-36.jpg')`
          }}
        ></div>
        
        {/* Floating Elements */}
        <div className="absolute top-16 left-8 w-16 h-16 bg-white/10 rounded-full animate-float"></div>
        <div className="absolute bottom-20 right-12 w-12 h-12 bg-accent/30 rounded-full animate-float" style={{animationDelay: '1s'}}></div>

        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-7xl font-sofia font-bold mb-6 animate-fade-in-up">
            {t('reviews.title')}
          </h1>
          <p className="text-xl md:text-2xl font-sofia font-light animate-fade-in-up opacity-90" style={{animationDelay: '0.2s'}}>
            {t('reviews.subtitle')}
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 " style={{ background: 'url("/assets/icons/top-img-bg.png") ' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div 
                key={index} 
                className="text-center animate-fade-in-up"
                style={{animationDelay: `${index * 0.1}s`}}
              >
                <div className="text-4xl mb-2">{stat.icon}</div>
                <div className="text-3xl font-sofia font-bold text-[#81736d] mb-1">{stat.value}</div>
                <div className="font-sofia text-text-secondary text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-fade-in-up">
            <h2 className="text-4xl font-sofia font-bold text-black mb-6">
              What Our Guests Say
            </h2>
            <p className="text-lg font-sofia text-text-secondary max-w-3xl mx-auto">
              Read authentic reviews from guests who have experienced the magic of Village Baidane
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {reviews.map((review, index) => (
              <Card 
                key={review.id} 
                hover 
                className="group cursor-pointer animate-fade-in-up"
                style={{animationDelay: `${index * 0.1}s`}}
                onClick={() => setSelectedReview(review)}
              >
                {/* Review Header */}
                <div className="flex items-center mb-4">
                  <div className="text-3xl mr-3">{review.avatar}</div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-sofia font-semibold text-black">{review.name}</h3>
                     
                    </div>
                    <div className="flex items-center justify-between">
                      <StarRating rating={review.rating} />
                      <span className="text-xs font-sofia text-text-light">{review.date}</span>
                    </div>
                  </div>
                </div>

                {/* Review Content */}
                <p className="text-text-secondary font-sofia text-sm leading-relaxed mb-4 line-clamp-4">
                  {review.text}
                </p>

                {/* Highlights */}
                {review.highlights && review.highlights.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {review.highlights.slice(0, 2).map((highlight, idx) => (
                      <span
                        key={idx}
                        className="text-xs font-sofia bg-accent/10 text-[#81736d] px-2 py-1 rounded-full"
                      >
                        {highlight}
                      </span>
                    ))}
                    {review.highlights.length > 2 && (
                      <span className="text-xs font-sofia text-text-light">
                        +{review.highlights.length - 2} more
                      </span>
                    )}
                  </div>
                )}

                {/* Read More */}
                <div className="text-accent font-sofia text-sm group-hover:text-accent/80 transition-colors duration-300">
                  Read full review →
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Review Modal */}
      {selectedReview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto animate-scale-in">
            <div className="p-8">
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <div className="text-4xl mr-4">{selectedReview.avatar}</div>
                  <div>
                    <h3 className="text-xl font-sofia font-bold text-black">{selectedReview.name}</h3>
                    <div className="flex items-center gap-4">
                      <StarRating rating={selectedReview.rating} />
                      <span className="text-sm font-sofia text-text-light">{selectedReview.date}</span>
                      
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedReview(null)}
                  className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors duration-200"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Full Review */}
              <p className="text-text-secondary font-sofia leading-relaxed mb-6">
                {selectedReview.text}
              </p>

              {/* All Highlights */}
              {selectedReview.highlights && selectedReview.highlights.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {selectedReview.highlights.map((highlight, idx) => (
                    <span
                      key={idx}
                      className="text-sm font-sofia bg-accent/10 text-[#81736d] px-3 py-1 rounded-full"
                    >
                      {highlight}
                    </span>
                  ))}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-4">
                <Button onClick={() => setSelectedReview(null)}>
                  {t('reviews.showLess')}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/booking')}
                >
                  {t('common.bookNow')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <section className="relative py-20 overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat bg-fixed"
          style={{
            backgroundImage: `url('/assets/images/villa Baidane-33.jpg')`
          }}
        ></div>
        {/* Overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-60"></div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <div className="animate-fade-in-up">
            <h2 className="text-3xl font-sofia font-bold mb-6">
              Ready to Create Your Own Story?
            </h2>
            <p className="text-lg font-sofia font-light mb-8 opacity-90">
              Join our community of satisfied guests and experience the magic of Village Baidane for yourself
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
              className="bg-[#81736d] hover:bg-[#81736d]"
                onClick={() => navigate('/booking')}
              >
                {t('common.bookNow')}
              </Button>
              <Button
                 variant="outline"
              size="lg"
              className="border-white text-white hover:bg-[#81736d] hover:text-white"
                onClick={() => navigate('/contact')}
              >
                {t('common.contactUs')}
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ReviewsScreen;
